import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  Divider,
} from "@mui/material";

// MUI Icons
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LockIcon from "@mui/icons-material/Lock";
import moment from "moment";

// Components
import ProfileBanner from "../../components/ProfileBanner";
import ArtistContent from "../../components/ArtistContent";
import TipPopup from "../../components/TipPopup";
import SongRequestButton from "../../components/Stream/SongRequestButton";
import HostPendingRequests from "../../components/Stream/HostPendingRequests";
import SocialProofLine from "../../components/SocialProofLine";
import FanProgression from "../../components/FanProgression";
import TopFansPanel from "../../components/TopFansPanel";
import SuccessPopup from "../../components/SuccessPopup";
import AssetPopup from "../../components/AssetsPopup";
import AddMusicToPlaylist from "../../components/AddMusicToPlaylist";
import ConfirmPopup from "../../components/ConfirmPopup";
import DirectChat from "../../components/DirectChat";
import VideoPopup from "../../components/VideoPlayer";
import Loader from "../../components/Loader";

// Custom components
import TrendingSection from "./components/TrendingSection";
import ProfileSocialLinks from "./components/ProfileSocialLinks";
import ProfileStats from "./components/ProfileStats";

// Hooks
import useArtistProfile from "./hooks/useArtistProfileSimple";
import useArtistProfileActions from "./hooks/useArtistProfileActions";

// Utils
import VerificationSVG from "../../assets/common/artistProfile/Verification.svg";
import VerificationGoldSVG from "../../assets/common/artistProfile/VerificationGold.svg";
import { fullName, isOwnerId } from "../../constants/function";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import GiftSubscription from "../../components/GiftSubscription";
import { addSongAction } from "../../action/songs";
import notifier from "../../components/Notifier";

import "./index.scss";

// The frame the top song and playlist cards use, so every panel in the row matches.
const PANEL_SX = {
  background: "#0C0C0C",
  border: "1px solid rgba(255, 255, 255, 0.06)",
  borderRadius: "16px",
  p: "18px",
};

export default function ArtistProfile() {
  const { t } = useTranslation("common");
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  
  // Get URL params
  const params = new URLSearchParams(history.location.search);
  const referralId = params.get("referralId");

  // UI State
  const [video, setVideo] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState(t("success.message7"));
  const [openTip, setOpenTip] = useState(false);
  const [openGift, setOpenGift] = useState(false);
  const [openSongRequest, setOpenSongRequest] = useState(false);
  const [open, setOpen] = useState(false);
  const [viewAsset, setViewAsset] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [selectedId, setSelectedId] = useState();
  const [menu, setMenu] = useState(false);

  // Use custom hooks - MUST be before any useEffect that uses these values
  const {
    loading,
    initialLoading,
    userData,
    trendingMusic,
    music,
    playlistMusic,
    nftMusic,
    albums,
    videos,
    getMyMusic,
    getPlayLists,
    getNfts,
    getAlbum,
    getVideos,
    getPlayListMusic,
    playlistNft,
    setUserData,
    setLoading,
    handleTabSwitch,
    handleScroll,
    active,
    loaded,
  } = useArtistProfile(id, referralId);
  
  // Debug loading states and user changes
  useEffect(() => {
    console.log('📊 Artist Profile Loading States:', {
      currentUrlId: id,
      loading,
      initialLoading,
      hasUserData: !!userData?._id,
      loadedUserId: userData?._id,
      userMismatch: id !== userData?._id && userData?._id
    });
    
    // Alert if we have a user mismatch (showing wrong user data)
    if (id && userData?._id && id !== userData._id) {
      console.error('⚠️ USER MISMATCH DETECTED!', {
        urlId: id,
        loadedUserId: userData._id,
        userName: (userData.firstName || '') + ' ' + (userData.lastName || '')
      });
    }
  }, [id, loading, initialLoading, userData]);

  // Validate ID and redirect if invalid
  useEffect(() => {
    if (!id || id === 'undefined' || id === 'null') {
      console.error('Invalid artist ID in URL:', id);
      notifier.error('Invalid artist profile');
      history.push('/app/Home');
    }
  }, [id, history]);
  
  // Redirect if user explicitly not found (not on timeout/error)
  useEffect(() => {
    if (!loading && !initialLoading && id && userData && !userData._id && !userData.error) {
      const timer = setTimeout(() => {
        if (userData && !userData._id && !userData.error) {
          console.error('Artist not found, redirecting to home');
          history.push('/app/Home');
        }
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [loading, initialLoading, userData, id, history]);

  const actions = useArtistProfileActions();

  // Handlers
  const handleTip = async (amount, id, type, currency) => {
    await actions.handleTip(amount, id, type, currency, setLoading, setOpenTip);
  };

  const subscribe = async () => {
    await actions.subscribe(id, userData, setUserData, setLoading, setOpen, setShowConfirm);
  };

  const unSubscribe = async () => {
    await actions.unSubscribe(id, userData, setUserData, setLoading, setMessage, setOpen);
  };

  const follow = async (artistId) => {
    await actions.follow(artistId, userData, setUserData, setLoading);
  };

  const handlePlayMusic = (music) => {
    if (!music["isSubscribed"] && music["private"]) {
      notifier.error(t("notifier.error.toast5"));
    } else {
      dispatch(addSongAction(music));
    }
  };

  const handlePlayPlaylist = (playlist) => {
    history.push(`/app/Music/${playlist?._id}`);
  };

  const handleAddToPlaylist = (musicId) => {
    setSelectedId(musicId);
    setShowPlaylist(true);
  };

  const handleClickOpen = () => {
    setViewAsset(true);
  };

  const handleClose = () => {
    setViewAsset(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClick = () => {
      if (menu) setMenu(false);
    };
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [menu]);

  // Reset chat when changing profiles
  useEffect(() => {
    setChatOpen(false);
  }, [id]);

  return (
    <Box sx={{ position: "relative", pb: 7 }}>
      <ProfileBanner
        userData={userData}
        edit={false}
        isArtist={true}
        loader={setLoading}
      />
      
      <Stack
        spacing={0.5}
        className="_artistProfileBox"
      >
        {/* Header: who they are on the left, what you can do on the right */}
        <Box className="ap-head">
          <Box className="ap-identity">
            {/* Name + Badges */}
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: "wrap", gap: "8px" }}>
              <Typography
                sx={{
                  fontSize: "28px",
                  fontWeight: 800,
                  color: "#FFF",
                  letterSpacing: "-0.5px",
                  lineHeight: 1.2,
                }}
              >
                {fullName(userData)}
              </Typography>
              {(userData["isVerified"] || userData["email"] === "chris@siki.io") && (
                <img
                  src={userData["email"] === "chris@siki.io" ? VerificationGoldSVG : VerificationSVG}
                  alt="Verified"
                  style={{ width: 20, height: 20, display: 'block', flexShrink: 0 }}
                />
              )}
              {/* Level Badge */}
              <Box
                onClick={isOwnerId(id) ? () => history.push("/app/Analytics") : undefined}
                sx={{
                  width: "22px",
                  height: "22px",
                  backgroundColor: "#111111",
                  border: "1.5px solid #FFEE20",
                  borderRadius: "5px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isOwnerId(id) ? "pointer" : "default",
                  userSelect: "none",
                  transition: "all 0.2s ease",
                  "&:hover": isOwnerId(id) ? {
                    transform: "scale(1.1)",
                    boxShadow: "0 0 8px rgba(255, 238, 32, 0.3)",
                  } : {},
                }}
                title={isOwnerId(id) ? "View your curator and creator stats" : undefined}
              >
                <Typography
                  align="center"
                  fontWeight="bold"
                  sx={{
                    color: "#FFEE20",
                    lineHeight: 1,
                    fontSize: "10px",
                    pointerEvents: "none",
                    userSelect: "none"
                  }}
                >
                  {userData["avgLevel"]}
                </Typography>
              </Box>
            </Stack>

            {/* Social proof: one quiet line under the name */}
            <SocialProofLine type="artist" id={id} />

            {/* Social links and follower counts share one line */}
            <Stack direction="row" alignItems="center" className="ap-meta-row">
              <ProfileSocialLinks
                twitter={userData?.twitterLink}
                instagram={userData?.instagramLink}
                tiktok={userData?.tikTokLink}
                youtube={userData?.youtubeLink}
              />
              <ProfileStats
                userData={userData}
                isOwnerId={isOwnerId(id)}
              />
            </Stack>
          </Box>

          {/* Action Buttons */}
          {!isOwnerId(id) && (
            <Stack
              direction="row"
              alignItems="center"
              className="ap-action-buttons"
              sx={{ gap: "8px", position: "relative" }}
            >
              {/* Follow Button */}
              <Button
                variant={userData["isFollowed"] ? "outlined" : "contained"}
                size="small"
                sx={{
                  height: "40px",
                  minWidth: "128px",
                  fontSize: "13px",
                  fontWeight: 600,
                  borderRadius: "10px",
                  textTransform: "none",
                  ...(userData["isFollowed"]
                    ? {
                        borderColor: "rgba(255, 238, 32, 0.3)",
                        color: "#FFEE20",
                        "&:hover": {
                          borderColor: "#FFEE20",
                          background: "rgba(255, 238, 32, 0.04)",
                        },
                      }
                    : {
                        background: "#FFEE20",
                        color: "#000",
                        "&:hover": {
                          background: "#FFE066",
                          boxShadow: "0 4px 20px rgba(255, 238, 32, 0.25)",
                        },
                      }),
                }}
                onClick={() => follow(userData["_id"])}
              >
                {userData["isFollowed"] === false
                  ? t("follow.followList.button1")
                  : t("follow.followList.button2")}
              </Button>

              {/* Subscribe Button */}
              {userData["subscriptionStatus"] === "enabled" && (
                userData["isSubscribed"] === true &&
                userData["subscribedStatus"] !== "Unsubscribed" ? (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => unSubscribe()}
                    sx={{
                      height: "40px",
                      fontSize: "12px",
                      fontWeight: 600,
                      borderRadius: "10px",
                      textTransform: "none",
                      borderColor: "rgba(255, 238, 32, 0.3)",
                      color: "#FFEE20",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        borderColor: "#FFEE20",
                        background: "rgba(255, 238, 32, 0.04)",
                      },
                    }}
                  >
                    Subscribed
                  </Button>
                ) : userData["isSubscribed"] === true &&
                  userData["subscribedStatus"] === "Unsubscribed" ? (
                  <Button
                    variant="outlined"
                    size="small"
                    disabled
                    sx={{
                      height: "40px",
                      fontSize: "12px",
                      fontWeight: 600,
                      borderRadius: "10px",
                      textTransform: "none",
                      borderColor: "rgba(255,255,255,0.06)",
                      color: "#555",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("artistProfile.button2")}
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowConfirm(true)}
                    sx={{
                      height: "40px",
                      fontSize: "12px",
                      fontWeight: 600,
                      borderRadius: "10px",
                      textTransform: "none",
                      borderColor: "rgba(255,255,255,0.06)",
                      background: "rgba(255,255,255,0.04)",
                      color: "#888",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        borderColor: "#FFEE20",
                        color: "#FFEE20",
                        background: "rgba(255, 238, 32, 0.04)",
                      },
                    }}
                  >
                    Subscribe
                  </Button>
                )
              )}

              {/* Gift a subscription. Mobile has offered this since the
                  endpoint shipped; the web had no way in at all. Shown on
                  the same terms as Subscribe, since there is nothing to gift
                  unless this artist sells one, and a fee of 0 is the schema
                  default rather than a free subscription. Not on your own
                  profile, where there is nobody to gift to. */}
              {userData["subscriptionStatus"] === "enabled" &&
              Number(userData["subscriptionFee"]) > 0 &&
              !isOwnerId(id) ? (
                <IconButton
                  onClick={() => setOpenGift(true)}
                  title={`Gift a subscription to ${fullName(userData)}`}
                  aria-label="Gift a subscription"
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    border: "1px solid rgba(255,255,255,0.06)",
                    background: "rgba(255,255,255,0.04)",
                    color: "#888",
                    "&:hover": {
                      borderColor: "#FFEE20",
                      color: "#FFEE20",
                      background: "rgba(255, 238, 32, 0.04)",
                    },
                  }}
                >
                  <CardGiftcardIcon sx={{ fontSize: 18 }} />
                </IconButton>
              ) : null}

              {/* Tip Button */}
              <IconButton
                onClick={() => setOpenTip(true)}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "4px !important",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  color: "#444",
                  transition: "all 0.2s",
                  "&:hover": { borderColor: "#FFEE20", color: "#FFEE20" },
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                </svg>
              </IconButton>

              {/* DM Button */}
              <IconButton
                onClick={() => setChatOpen(true)}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "4px !important",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  color: "#444",
                  transition: "all 0.2s",
                  "&:hover": { borderColor: "#FFEE20", color: "#FFEE20" },
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                </svg>
              </IconButton>

              {/* Song Request */}
              <Button
                onClick={() => setOpenSongRequest(true)}
                sx={{
                  height: "40px",
                  px: 2,
                  fontSize: "13px",
                  fontWeight: 600,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  color: "#888",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "#FFEE20",
                    color: "#FFEE20",
                    background: "rgba(255, 238, 32, 0.04)",
                  },
                }}
              >
                Song Request
              </Button>
            </Stack>
          )}
        </Box>

        {/* Bio: full width under the header */}
        {userData?.bio && (
          <Typography
            className="ap-bio"
            sx={{
              fontSize: "14px",
              color: "#888",
              lineHeight: 1.5,
            }}
          >
            {userData.bio}
          </Typography>
        )}

        {/* One row across the page: the viewer's standing (or the owner's queues)
            beside the top song and playlist, sharing the width equally */}
        <Box className="ap-panels">
          {!isOwnerId(id) && <FanProgression artistId={id} artistName={fullName(userData)} sx={PANEL_SX} />}
          {isOwnerId(id) && (
            <Box sx={PANEL_SX}>
              <HostPendingRequests />
            </Box>
          )}
          {isOwnerId(id) && <TopFansPanel artistId={id} sx={PANEL_SX} />}
          <TrendingSection
            trendingMusic={trendingMusic}
            onPlayMusic={handlePlayMusic}
            onPlayPlaylist={handlePlayPlaylist}
            onAddToPlaylist={handleAddToPlaylist}
            sx={PANEL_SX}
          />
        </Box>

        {/* Artist Content Tabs */}
        <Stack>
          <ArtistContent
            id={id}
            userData={userData}
            getPlayListMusic={getPlayListMusic}
            playlistNft={playlistNft}
            initialMusic={music}
            initialPlaylists={playlistMusic}
            initialNfts={nftMusic}
            initialAlbums={albums}
            initialVideos={videos}
            liveChannelName={userData?.liveChannelName || null}
            getMyMusic={getMyMusic}
            getPlayLists={getPlayLists}
            getNfts={getNfts}
            getAlbum={getAlbum}
            getVideos={getVideos}
            handleTabSwitch={handleTabSwitch}
            handleScroll={handleScroll}
            active={active}
            loaded={loaded}
            loading={loading}
          />
        </Stack>
      </Stack>


      {/* Popups and Modals */}
      {chatOpen && (
        <DirectChat
          senderUserId={userData["_id"]}
          title={fullName(userData)}
          onOutsideClick={() => setChatOpen(false)}
          onOpenTip={() => setOpenTip(true)}
          artistChat={true}
        />
      )}
      
      {openTip && (
        <TipPopup
          artist={fullName(userData)}
          id={userData["_id"]}
          open={openTip}
          isArtist={true}
          handleClose={handleTip}
        />
      )}

      {openSongRequest && userData?._id && (
        <SongRequestButton
          hostId={userData._id}
          open={openSongRequest}
          onClose={() => setOpenSongRequest(false)}
        />
      )}
      
      {open && (
        <SuccessPopup
          message={message}
          open={open}
          handleClose={() => setOpen(false)}
        />
      )}
      
      {viewAsset && (
        <AssetPopup
          id={userData["_id"]}
          open={viewAsset}
          handleClose={handleClose}
        />
      )}

      {showPlaylist && (
        <AddMusicToPlaylist
          musicId={selectedId}
          open={showPlaylist}
          handleClose={() => setShowPlaylist(false)}
        />
      )}
      
      {initialLoading && <Loader />}
      
      {showConfirm && (
        <ConfirmPopup
          open={showConfirm}
          id={userData["_id"]}
          index={0}
          message={`${t("confirmPopup.text3")} ${userData["subscriptionFee"]} SIKI coins.`}
          handleClose={(e) => (e ? subscribe() : setShowConfirm(false))}
          loading={loading}
        />
      )}

      {openGift && (
        <GiftSubscription
          open={openGift}
          onClose={() => setOpenGift(false)}
          artistId={id}
          artistName={fullName(userData)}
          monthlyFee={userData["subscriptionFee"]}
        />
      )}
      
      {video && (
        <VideoPopup
          open={video ? true : false}
          video={video}
          handleClose={() => setVideo(null)}
        />
      )}
    </Box>
  );
}